import { useQuery } from "@apollo/client";
import { Alert, AlertTitle, Box, Stack, Typography, useTheme } from "@mui/material";
import { gql } from "generated-graphql";
import { useReport } from "routes/Customer/Chemicals/Report/useReport";

export const FACILITY_CUPA_PREFERENCES_QUERY = gql(`
  query FacilityCupaPreferences($facilityId: ID!) {
    facilityCupaPreferences(facilityId: $facilityId) {
      cupaName
      summary
      preferences {
        title
        recommendation
        confidence
      }
    }
  }
`);

const CONFIDENCE_LABELS: Record<string, string> = {
  high: "High",
  medium: "Medium",
};

/**
 * FYI panel for the Verify & Submit step: the facility's CUPA's known
 * preferences, so the preparer can catch CUPA-specific issues before filing.
 * Purely informational — never blocks or gates submission, and renders
 * nothing whenever there is nothing useful to show (non-CA facility, no
 * CUPA, tenant flag off, no profile, loading, or error).
 */
export function CupaPreferencesChecklist() {
  const theme = useTheme();
  const { data: report } = useReport();
  const facility = report?.tierIIReport?.facility;
  const facilityId = facility?.id;
  const skip = !facilityId || facility?.state !== "CA" || !facility?.lepcId;

  const { data, loading, error } = useQuery(FACILITY_CUPA_PREFERENCES_QUERY, {
    variables: facilityId ? { facilityId } : undefined,
    skip,
    fetchPolicy: "cache-and-network",
  });

  const prefs = data?.facilityCupaPreferences;
  if (skip || loading || error || !prefs || prefs.preferences.length === 0) {
    return <></>;
  }

  return (
    <Alert severity="info" sx={{ mb: theme.spacing(2) }}>
      <AlertTitle>Before you submit — {prefs.cupaName} preferences</AlertTitle>
      {prefs.summary ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {prefs.summary}
        </Typography>
      ) : null}
      <Stack component="ul" gap={1} sx={{ m: 0, pl: 2.5 }}>
        {prefs.preferences.map((pref, index) => (
          <Box component="li" key={index}>
            <Typography variant="body2">
              <strong>{pref.title}</strong> — {pref.recommendation}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Confidence: {CONFIDENCE_LABELS[pref.confidence] ?? pref.confidence}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Alert>
  );
}
